/**
 * RFQ Supplier Search Dashboard Component
 * 
 * Displays products in RFQ with supplier search functionality.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import DataDashboard from '../../../common/DataDashboard';
import AdminLayout from '../../layout/AdminLayout';
import UserLayout from '../../../user-v2/layout/UserLayout';
import HeaderActions from '../../../common/HeaderActions';
import { createStandardHeaderActions } from '../../../common/HeaderActions/helpers';
import ExportModal from '../../../common/ExportModal';
import { AdminUser } from '../../../../types/admin-v2';
import { API_ENDPOINTS } from '../../../../utils/config';
import { useAuth } from '../../../../context/AuthContext';
import { CATEGORIES, getCategoryById } from '../../../../utils/categories';
import { getDynamicSubItems } from '../../../../utils/getDynamicSubItems';
import { getRFQNavigationInfo, getRFQPageRoute, getRFQPageName } from '../../../../utils/rfqNavigation';
import { useRfqLock } from '../../../../hooks/useRfqLock';
import RfqLockedModal from '../../../common/RfqLockedModal';

interface GroupedSupplier {
  id: string;
  supplier_id: string;
  supplier_name: string;
  supplier_readable_id?: string;
  supplier_source?: string;
  rating?: number;
  products: Array<{
    supplier_product_id: string;
    product_name: string;
    price?: string | { min?: number; max?: number };
    currency?: string;
    category?: string;
    request_product_id: string;
  }>;
  company_specific_codes?: any[];
}

interface RFQSupplierSearchDashboardProps {
  user: AdminUser;
  currentRoute: string;
  onNavigate: (route: string) => void;
  onLogout: () => void;
}

const RFQSupplierSearchDashboard: React.FC<RFQSupplierSearchDashboardProps> = ({
  user,
  currentRoute,
  onNavigate,
  onLogout
}) => {
  const { token } = useAuth();
  const location = useLocation();

  // View Mode State
  const [viewMode, setViewMode] = useState<'suppliers' | 'products_list' | 'brave_search'>('suppliers');

  // Navigation actions
  const navInfo = useMemo(() => getRFQNavigationInfo(currentRoute), [currentRoute]);



  const navigationActions = useMemo(() => {
    // Only show RFQ navigation in 'suppliers' mode (Default View)
    if (viewMode !== 'suppliers') return undefined;

    const hasPreviousPage = navInfo?.previousPage;
    const hasNextPage = navInfo?.nextPage;

    if (!hasPreviousPage && !hasNextPage) {
      return undefined;
    }

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', width: '100%', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {hasPreviousPage && navInfo?.previousPage && (
            <button
              onClick={() => {
                if (navInfo && navInfo.previousPage) {
                  onNavigate(getRFQPageRoute(navInfo.baseRoute, navInfo.requestId, navInfo.previousPage));
                }
              }}
              className="rfq-nav-button"
              style={{
                padding: '8px 16px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                backgroundColor: '#ffffff',
                color: '#374151',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              ← Back to {getRFQPageName(navInfo.previousPage)}
            </button>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {hasNextPage && navInfo?.nextPage && (
            <button
              onClick={() => {
                if (navInfo.nextPage) {
                  onNavigate(getRFQPageRoute(navInfo.baseRoute, navInfo.requestId, navInfo.nextPage));
                }
              }}
              className="rfq-nav-button"
              style={{
                padding: '8px 16px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                backgroundColor: '#ffffff',
                color: '#374151',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              Next to {navInfo.nextPage ? getRFQPageName(navInfo.nextPage) : ''} →
            </button>
          )}
        </div>
      </div>
    );
  }, [navInfo, onNavigate, viewMode]);

  // Extract requestId and productId from URL
  const { requestId, productId } = useMemo(() => {
    const pathParts = location.pathname.split('/');
    const openIndex = pathParts.indexOf('open');
    const rfqIndex = pathParts.indexOf('rfq'); // For /dashboard/user/rfq/ pattern
    const suppliersIndex = pathParts.indexOf('suppliers');

    let reqId = null;
    let prodId = null;

    // Support both /dashboard/rfqs/open/:id and /dashboard/user/rfq/:id patterns
    if (openIndex !== -1 && pathParts.length > openIndex + 1) {
      reqId = pathParts[openIndex + 1];
    } else if (rfqIndex !== -1 && pathParts.length > rfqIndex + 1) {
      reqId = pathParts[rfqIndex + 1];
    }

    if (suppliersIndex !== -1 && pathParts.length > suppliersIndex + 1) {
      prodId = pathParts[suppliersIndex + 1];
    }

    return { requestId: reqId, productId: prodId };
  }, [location.pathname]);

  // Determine base route based on current path
  const baseRoute = useMemo(() => {
    if (location.pathname.startsWith('/dashboard/user/rfq/')) {
      return '/dashboard/user/rfq';
    }
    return '/dashboard/rfqs/open';
  }, [location.pathname]);



  // Data States
  const [products, setProducts] = useState<any[]>([]);
  const [supplierProducts, setSupplierProducts] = useState<any[]>([]); // For Brave Search results
  const [groupedSuppliers, setGroupedSuppliers] = useState<GroupedSupplier[]>([]); // For Supplier List view
  const [expandedSuppliers, setExpandedSuppliers] = useState<Set<string>>(new Set());

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [selectedProductName, setSelectedProductName] = useState<string | null>(null);
  const [selectedProductDetails, setSelectedProductDetails] = useState<any>(null);
  const [searchMode, setSearchMode] = useState<'list' | 'results'>('list'); // Internal mode for Brave Search flow
  const [hasAutoLoaded, setHasAutoLoaded] = useState(false);
  const [paginationInfo, setPaginationInfo] = useState({
    page: 1,
    limit: 25,
    total: 0,
    pages: 0,
    has_next: false,
    has_prev: false
  });

  // Brave Search states
  const [showBraveModal, setShowBraveModal] = useState(false);
  const [braveSearchFields, setBraveSearchFields] = useState({
    name: true,
    brand: true,
    category: false,  // Default OFF because it's UNSPSC codes
    description: false
  });
  const [isBraveSearching, setIsBraveSearching] = useState(false);
  const [braveSearchOffset, setBraveSearchOffset] = useState(0);
  const [braveSearchLimit] = useState(20);  // Max results per page (Brave API max)
  const [additionalSearchText, setAdditionalSearchText] = useState('');
  const [selectedSupplierIds, setSelectedSupplierIds] = useState<string[]>([]);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);  // Selected product IDs for list mode
  const [rfqSelectedSupplierIds, setRfqSelectedSupplierIds] = useState<string[]>([]);  // GLOBAL: Selected Supplier IDs for entire RFQ
  const [rfqSelectedSuppliersDetails, setRfqSelectedSuppliersDetails] = useState<any[]>([]);  // Details of selected suppliers
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [supplierToApprove, setSupplierToApprove] = useState<any>(null);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  // SSE Streaming states
  const [streamingProgress, setStreamingProgress] = useState<any[]>([]);
  const [streamingStatus, setStreamingStatus] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(10);
  const [foundCount, setFoundCount] = useState<number>(0);
  const [duplicateCount, setDuplicateCount] = useState<number>(0);

  // Effect: Persist selected suppliers to backend when changed (Debounced)
  const isFirstRun = useRef(true);
  useEffect(() => {
    // Skip initial run to prevent overwriting with empty array
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }

    if (!requestId || !token) return;

    const timeoutId = setTimeout(async () => {
      try {
        console.log('💾 Persisting selected suppliers to backend:', rfqSelectedSupplierIds);
        const response = await fetch(`${API_ENDPOINTS.REQUEST_V2_DETAIL(requestId)}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            selected_supplier_ids: rfqSelectedSupplierIds
          })
        });

        const result = await response.json();
        if (result.success) {
          console.log('✅ Successfully persisted selection');
        } else {
          console.error('❌ Failed to persist selection:', result.error);
        }
      } catch (err) {
        console.error('❌ Error persisting selection:', err);
      }
    }, 1000); // 1s debounce

    return () => clearTimeout(timeoutId);
  }, [rfqSelectedSupplierIds, requestId, token]);

  // Effect: Fetch Suppliers + Products when in "suppliers" mode
  useEffect(() => {
    if (viewMode === 'suppliers') {
      loadAllSuppliers();
    }
  }, [viewMode, requestId, token]);

  // Get category name helper function
  const getCategoryName = (code: string): string => {
    const category = getCategoryById(code);
    return category?.name.tr || code;
  };

  console.log('🔍 RFQSupplierSearchDashboard mounted');
  console.log('🆔 Request ID:', requestId);
  console.log('🆔 Product ID from URL:', productId);
  console.log('🔑 Token exists:', !!token);
  console.log('📍 Current path:', location.pathname);
  console.log('🎭 Search Mode:', searchMode);
  console.log('📦 Supplier Products Count:', supplierProducts.length);

  // Load products
  const loadProducts = async () => {
    if (!requestId || !token) return;

    setIsLoading(true);
    setError(null);

    try {
      // Load products
      const response = await fetch(API_ENDPOINTS.REQUEST_V2_PRODUCTS(requestId), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.data) {
        // Transform products for display
        const transformedProducts = data.data.map((product: any) => ({
          ...product,
          id: product._id,
          supplier_count: product.supplier_count || 0,  // From backend
          brave_search_state: product.brave_search_state || null
        }));

        setProducts(transformedProducts);
        setPaginationInfo({
          page: 1,
          limit: data.data.length,
          total: data.data.length,
          pages: 1,
          has_next: false,
          has_prev: false
        });

        // Load RFQ details to get selected supplier_product_ids
        const rfqResponse = await fetch(API_ENDPOINTS.REQUEST_V2_DETAIL(requestId), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const rfqResult = await rfqResponse.json();

        if (rfqResult.success && rfqResult.data) {
          const selectedSuppliers = (rfqResult.data.selected_supplier_ids || []).map(String);
          console.log('✅ RFQ selected supplier_ids (GLOBAL):', selectedSuppliers);
          setRfqSelectedSupplierIds(selectedSuppliers);

          // Fetch supplier details for footer display
          if (selectedSuppliers.length > 0) {
            try {
              const supplierDetailsPromises = selectedSuppliers.map(async (supplierId: string) => {
                try {
                  const response = await fetch(API_ENDPOINTS.SUPPLIER_DETAILS(supplierId), {
                    headers: {
                      'Authorization': `Bearer ${token}`
                    }
                  });
                  const result = await response.json();
                  return result.success ? result.data : null;
                } catch (err) {
                  console.error('Error fetching supplier:', supplierId, err);
                  return null;
                }
              });

              const supplierDetails = (await Promise.all(supplierDetailsPromises)).filter(Boolean);
              console.log('✅ Loaded supplier details for footer:', supplierDetails);
              setRfqSelectedSuppliersDetails(supplierDetails);
            } catch (err) {
              console.error('⚠️ Could not load supplier details:', err);
            }
          } else {
            setRfqSelectedSuppliersDetails([]);
          }
        }

        console.log('📦 Loaded products with supplier counts:', transformedProducts.map((p: any) => ({
          name: p.product_name,
          supplier_count: p.supplier_count,
          brave_offset: p.brave_search_state?.offset
        })));
      } else {
        throw new Error(data.message || 'Failed to load products');
      }
    } catch (error: any) {
      console.error('Error fetching products:', error);
      setError(error.message || 'Failed to load products');
    } finally {
      setIsLoading(false);
    }
  };

  // Load all suppliers for user's company (Supplier-Centric View)
  const loadAllSuppliers = async () => {
    if (!requestId || !token) return;

    setIsLoading(true);
    setError(null);

    try {
      // Load suppliers
      const suppliersResponse = await fetch(API_ENDPOINTS.REQUEST_USER_SUPPLIERS(requestId), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!suppliersResponse.ok) {
        throw new Error(`HTTP error! status: ${suppliersResponse.status}`);
      }

      const suppliersData = await suppliersResponse.json();

      if (!suppliersData.success || !suppliersData.data) {
        throw new Error(suppliersData.message || 'Failed to load suppliers');
      }

      // Transform suppliers data
      const transformedSuppliers: GroupedSupplier[] = suppliersData.data.map((supplier: any) => ({
        id: supplier.supplier_id || supplier._id,
        supplier_id: supplier.supplier_id || supplier._id,
        supplier_name: supplier.supplier_name || 'Unknown Supplier',
        supplier_readable_id: supplier.supplier_readable_id,
        supplier_source: supplier.supplier_source || 'manual',
        rating: supplier.rating || 0,
        products: supplier.products || [],
        company_specific_codes: supplier.company_specific_codes || []
      }));

      setGroupedSuppliers(transformedSuppliers);

      // Load RFQ details for global selection
      const rfqResponse = await fetch(API_ENDPOINTS.REQUEST_V2_DETAIL(requestId), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const rfqResult = await rfqResponse.json();

      if (rfqResult.success && rfqResult.data) {
        const selectedSuppliers = (rfqResult.data.selected_supplier_ids || []).map(String);
        setRfqSelectedSupplierIds(selectedSuppliers);

        // Fetch details for footer
        if (selectedSuppliers.length > 0) {
          const detailsPromises = selectedSuppliers.map(async (supplierId: string) => {
            try {
              const response = await fetch(API_ENDPOINTS.SUPPLIER_DETAILS(supplierId), {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              const result = await response.json();
              return result.success ? result.data : null;
            } catch (err) {
              return null;
            }
          });
          const details = (await Promise.all(detailsPromises)).filter(Boolean);
          setRfqSelectedSuppliersDetails(details);
        } else {
          setRfqSelectedSuppliersDetails([]);
        }
      }
    } catch (error: any) {
      console.error('Error fetching suppliers:', error);
      setError(error.message || 'Failed to load suppliers');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Initial Load Logic
    if (productId) {
      // Deep link to product -> Load products & trigger auto-search logic
      loadProducts();
    } else {
      // Default -> Load Supplier List
      loadAllSuppliers();
      // Also load products in background to be ready for Search Mode
      loadProducts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId, token]);

  // Reset auto-load flag when productId changes
  useEffect(() => {
    console.log('🔄 productId changed, resetting hasAutoLoaded flag');
    setHasAutoLoaded(false);
  }, [productId]);

  // Auto-load suppliers if productId is in URL (only once per productId)
  useEffect(() => {
    console.log('🔍 Auto-load useEffect triggered:', {
      productId,
      productsLength: products.length,
      hasAutoLoaded,
      isLoading
    });

    if (productId && products.length > 0 && !hasAutoLoaded && !isLoading) {
      console.log('🔄 Auto-loading suppliers for product ID from URL:', productId);
      const product = products.find((p: any) => (p.id || p._id) === productId);

      if (product) {
        const productName = product.product_name || product.name;
        console.log('✅ Found product in list:', productName);
        setSelectedProductId(productId);
        setSelectedProductName(productName);
        setSelectedProductDetails(product);

        // Load brave_search_state if exists
        if (product.brave_search_state) {
          const braveState = product.brave_search_state;
          console.log('📊 Loading saved Brave Search state:', braveState);
          setBraveSearchOffset(braveState.offset || 0);
          if (braveState.search_fields_used) {
            const fields = braveState.search_fields_used;
            setBraveSearchFields({
              name: fields.includes('name'),
              brand: fields.includes('brand'),
              category: fields.includes('category'),
              description: fields.includes('description')
            });
          }
          if (braveState.additional_text) {
            setAdditionalSearchText(braveState.additional_text);
          }
        }

        setHasAutoLoaded(true); // Mark as loaded to prevent re-triggering
        console.log('✅ Calling loadSuppliersForProduct from auto-load with productId:', productId);
        loadSuppliersForProduct(productId, productName, product);
      } else {
        console.log('⚠️ Product not found in list, product ID:', productId);
      }
    } else {
      console.log('⏭️ Skipping auto-load:', {
        hasProductId: !!productId,
        hasProducts: products.length > 0,
        alreadyLoaded: hasAutoLoaded,
        currentlyLoading: isLoading
      });
    }
  }, [productId, products, hasAutoLoaded, isLoading]);


  // Toggle supplier expansion
  const toggleSupplierExpansion = (rowId: string) => {
    setExpandedSuppliers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  // Format price for display
  const formatPrice = (price: any, currency?: string): string => {
    if (!price) return '-';
    let priceStr = '';
    if (typeof price === 'string') {
      priceStr = price;
    } else if (typeof price === 'object') {
      if (price.min !== undefined && price.max !== undefined) {
        priceStr = `${price.min} - ${price.max}`;
      } else if (price.min !== undefined) {
        priceStr = `${price.min}+`;
      } else if (price.max !== undefined) {
        priceStr = `up to ${price.max}`;
      } else {
        priceStr = String(price);
      }
    } else {
      priceStr = String(price);
    }

    if (currency && priceStr !== '-') {
      return `${priceStr} ${currency}`;
    }
    return priceStr;
  };

  // Column definitions for Supplier List (Default View)
  // Column definitions for Supplier List (Default View)
  const supplierColumns = [
    {
      key: 'supplier_name',
      title: 'Supplier Name',
      type: 'text' as const,
      sortable: true,
      editable: false,
      width: '250px'
    },
    {
      key: 'supplier_readable_id',
      title: 'Supplier ID',
      type: 'text' as const,
      sortable: true,
      editable: false,
      width: '150px',
      render: (value: string, row: GroupedSupplier) => value || row.supplier_id
    },
    {
      key: 'supplier_source',
      title: 'Source',
      type: 'text' as const,
      sortable: true,
      editable: false,
      width: '120px',
      render: (value: string) => {
        if (value === 'ai_brave') {
          return (
            <span style={{
              padding: '4px 8px',
              backgroundColor: '#fff3cd',
              color: '#856404',
              borderRadius: '4px',
              fontSize: '12px'
            }}>
              AI Brave
            </span>
          );
        }
        return <span style={{ color: '#666' }}>Manual</span>;
      }
    },
    {
      key: 'approval_status',
      title: 'Approval Status',
      type: 'text' as const,
      sortable: true,
      editable: false,
      width: '140px',
      render: (value: string, row: GroupedSupplier) => {
        // Find status for current user's company
        const userCompanyId = user?.company_id;
        let status = 'pending';

        if (userCompanyId && row.company_specific_codes) {
          const codeEntry = row.company_specific_codes.find(
            (code: any) => String(code.company_id) === String(userCompanyId)
          );
          if (codeEntry?.approval_status) {
            status = String(codeEntry.approval_status).toLowerCase();
          }
        }

        // Render colored badge
        let backgroundColor = '#fff3cd'; // yellow/pending
        let color = '#856404';
        let label = 'Pending';

        if (status === 'approved') {
          backgroundColor = '#d1e7dd';
          color = '#0f5132';
          label = 'Approved';
        } else if (status === 'rejected') {
          backgroundColor = '#f8d7da';
          color = '#842029';
          label = 'Rejected';
        }

        return (
          <span style={{
            padding: '4px 8px',
            backgroundColor,
            color,
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 500,
            textTransform: 'capitalize'
          }}>
            {label}
          </span>
        );
      }
    },
    {
      key: 'rating',
      title: 'Rating',
      type: 'number' as const,
      sortable: true,
      editable: false,
      width: '100px',
      render: (value: number) => value ? `${value}/5` : '-'
    },
    {
      key: 'products_count',
      title: 'Products',
      type: 'number' as const,
      sortable: true,
      editable: false,
      width: '100px',
      render: (value: any, row: GroupedSupplier) => `${row.products.length} product(s)`
    }
  ];

  // Column definitions for product list
  const productColumns = [
    {
      key: 'line_number',
      title: '#',
      type: 'number' as const,
      sortable: true,
      editable: false,
      width: '60px'
    },
    {
      key: 'product_name',
      title: 'Product Name',
      type: 'text' as const,
      sortable: true,
      editable: false,
      width: '250px'
    },
    {
      key: 'product_readable_id',
      title: 'Product ID',
      type: 'text' as const,
      sortable: true,
      editable: false,
      width: '120px',
      render: (value: string) => value || '-'
    },
    {
      key: 'supplier_count',
      title: 'Suppliers',
      type: 'number' as const,
      sortable: true,
      editable: false,
      width: '100px'
    },
    {
      key: 'brave_search_page',
      title: 'Brave Page',
      type: 'text' as const,
      sortable: false,
      editable: false,
      width: '100px',
      render: (value: any, row: any) => {
        const state = row.brave_search_state;
        if (state && state.offset) {
          const page = Math.floor(state.offset / 5) + 1;
          return `${page} (${state.search_count || 1}x)`;
        }
        return '-';
      }
    }
  ];

  // Column definitions for supplier products
  const supplierProductColumns = [

    {
      key: 'supplier_product_id',
      title: 'Supplier Product ID',
      type: 'text' as const,
      sortable: true,
      editable: false,
      width: '150px'
    },
    {
      key: 'source',
      title: 'Source',
      type: 'action' as const,
      sortable: true,
      editable: false,
      width: '140px',
      render: (value: string, row: any) => {
        if (value === 'ai_brave') {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span>AI Brave</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleApproveSupplier(row);
                }}
                style={{
                  padding: '2px 8px',
                  fontSize: '11px',
                  backgroundColor: '#fff',
                  border: '1px solid #ccc',
                  borderRadius: '3px',
                  cursor: 'pointer'
                }}
              >
                Approve
              </button>
            </div>
          );
        }
        return value || 'Manual';
      }
    },
    {
      key: 'category',
      title: 'Category',
      type: 'text' as const,
      sortable: true,
      editable: false,
      width: '200px',
      render: (value: string) => {
        if (!value) return '-';

        // If it's a comma-separated list of codes, split and map
        const codes = value.split(',').map((c: string) => c.trim());
        const mappedNames = codes.map((code: string) => getCategoryName(code));
        return mappedNames.join(', ');
      }
    },
    {
      key: 'supplier_name',
      title: 'Name',
      type: 'text' as const,
      sortable: true,
      editable: false,
      width: '200px'
    },
    {
      key: 'rating',
      title: 'Rating',
      type: 'number' as const,
      sortable: true,
      editable: false,
      width: '80px'
    },
    {
      key: 'price',
      title: 'Price',
      type: 'price_range' as const,
      sortable: true,
      editable: false,
      width: '120px'
    },
    {
      key: 'last_update',
      title: 'Last Update',
      type: 'date' as const,
      sortable: true,
      editable: false,
      width: '150px'
    }
  ];

  const handleDataFetch = async (params: {
    page: number;
    limit: number;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
    filters?: Record<string, string>;
  }) => {
    // Products are already loaded, no need to refetch
    return;
  };

  const handleExport = async (exportData: any) => {
    console.log('Export data:', exportData);
  };

  const handleSelectionChange = (selectedIds: string[]) => {
    if (viewMode === 'suppliers') {
      // Main supplier list selection - IDs are directly supplier_ids

      // Filter out unapproved AI suppliers
      const validSelectedIds = selectedIds.filter(id => {
        // Find supplier in groupedSuppliers
        const supplier = groupedSuppliers.find(s => String(s.supplier_id) === String(id));
        if (supplier) {
          const source = supplier.supplier_source || (supplier as any).source;
          if (source === 'ai_brave') {
            // For now, blocking all AI selection until approved. 
            // If we had approval status in GroupedSupplier, we could check it.
            // Assuming unapproved if in this list and source is AI.
            // (User dashboard might handle this differently, but strict validation is safer)
            alert(`⚠️ Supplier "${supplier.supplier_name}" was added by AI. Please approve it first before selecting.`);
            return false;
          }
        }
        return true;
      });

      setRfqSelectedSupplierIds(validSelectedIds);

      // Update details for footer immediately to ensure responsiveness
      if (validSelectedIds.length > 0) {
        // Filter existing details to keep only selected ones, preventing clearing of cache
        // Fetch missing details if necessary (handled by useEffect usually)
      } else {
        setRfqSelectedSuppliersDetails([]);
      }
    } else if (searchMode === 'list') {
      // Product list mode
      setSelectedProductIds(selectedIds);
      if (selectedIds.length === 1) {
        setSelectedProductId(selectedIds[0]);
      } else {
        setSelectedProductId(null);
      }
    } else {
      // Supplier results mode - selectedIds are supplier_product IDs, convert to supplier_ids
      const supplierIds = supplierProducts
        .filter(sp => selectedIds.includes(sp.supplier_product_id || sp._id || sp.id))
        .map(sp => String(sp.supplier_id));
      // Remove duplicates using Array.from instead of spread operator
      setSelectedSupplierIds(Array.from(new Set(supplierIds)));
    }
  };

  // Separate function to load suppliers for a product
  const loadSuppliersForProduct = async (prodId: string, productName: string, productDetails?: any) => {
    setIsLoading(true);
    try {
      console.log('🔄 Fetching suppliers for:', productName);
      console.log('📦 Product details received:', productDetails);

      // Reload RFQ selected suppliers when changing products
      try {
        const rfqResponse = await fetch(API_ENDPOINTS.REQUEST_V2_DETAIL(requestId!), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const rfqResult = await rfqResponse.json();

        if (rfqResult.success && rfqResult.data) {
          const selectedSuppliers = (rfqResult.data.selected_supplier_ids || []).map(String);
          console.log('🔄 Reloaded RFQ selected supplier_ids (GLOBAL):', selectedSuppliers);
          setRfqSelectedSupplierIds(selectedSuppliers);
        }
      } catch (err) {
        console.error('⚠️ Could not reload RFQ selections:', err);
      }

      // Store product details for modal - fetch full details if not provided
      if (productDetails) {
        console.log('📦 Setting initial product details:', productDetails);
        setSelectedProductDetails(productDetails); // Set immediately so modal shows something

        // If we have request_product data, try to enrich it with products_tr data
        const requestProduct = productDetails;
        const productId = requestProduct.product_id || requestProduct._id;

        console.log('🔍 Looking for product_id:', productId);
        console.log('🔍 Request product keys:', Object.keys(requestProduct));
        console.log('🔍 Request product data:', {
          name: requestProduct.product_name || requestProduct.name,
          brand: requestProduct.brand || requestProduct.product_brand,
          category: requestProduct.category,
          description: requestProduct.description
        });

        if (productId) {
          try {
            console.log('📡 Fetching full product details from:', API_ENDPOINTS.PRODUCT_DETAILS(productId));

            // Fetch full product details from products_tr
            const productResponse = await fetch(`${API_ENDPOINTS.PRODUCT_DETAILS(productId)}`, {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });

            console.log('📥 Product details response status:', productResponse.status);

            if (productResponse.ok) {
              const productData = await productResponse.json();
              console.log('📥 Product details response:', productData);

              if (productData.success && productData.data) {
                console.log('✅ Enriched product details from products_tr');
                console.log('   - Name:', productData.data.name);
                console.log('   - Brand:', productData.data.brand);
                console.log('   - Category:', productData.data.category);
                console.log('   - Description:', productData.data.description?.substring(0, 50));

                const enrichedDetails = {
                  ...requestProduct,
                  ...productData.data // Merge with products_tr data
                };
                console.log('✅ Final enriched details:', enrichedDetails);
                setSelectedProductDetails(enrichedDetails);
              } else {
                console.log('⚠️ Product data not in expected format');
                setSelectedProductDetails(requestProduct);
              }
            } else {
              console.log('⚠️ Product details request failed');
              setSelectedProductDetails(requestProduct);
            }
          } catch (err) {
            console.error('❌ Error fetching product details:', err);
            setSelectedProductDetails(requestProduct);
          }
        } else {
          console.log('⚠️ No product_id found, using request_product as-is');
          setSelectedProductDetails(productDetails);
        }
      }

      console.log('📡 API Call - Request ID:', requestId);
      console.log('📡 API Call - Product Name:', productName);
      console.log('📡 API Call - Product ID (prodId):', prodId);

      const response = await fetch(`${API_ENDPOINTS.REQUESTS_V2}/${requestId}/suppliers/search`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          product_name: productName,
          request_product_id: prodId  // Send the request_product _id for precise matching
        })
      });

      const data = await response.json();
      console.log('🔍 Supplier Search Response:', data);
      console.log('📦 Supplier Products Data:', data.data);
      console.log('📊 Count:', data.data?.length);

      if (data.success && data.data) {
        console.log('✅ Setting supplier products:', data.data);
        setSupplierProducts(data.data);
        setSearchMode('results');
        console.log('✅ Search mode set to results');

        // No auto-selection needed - checkbox already uses rfqSelectedSupplierIds (global)
      } else {
        console.log('❌ No suppliers found or error');
        setError(data.error || 'No suppliers found for this product');
      }
    } catch (error: any) {
      console.error('❌ Error fetching supplier products:', error);
      setError(error.message || 'Failed to search suppliers');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpen = async (row?: any) => {
    console.log('🚀 handleOpen CALLED!');
    console.log('📍 Row passed:', row);
    console.log('📍 Row type:', typeof row);

    // Row can be either an ID string or an object with id/_id
    let productIdToUse: string | null = null;

    if (typeof row === 'string') {
      productIdToUse = row;
    } else if (row && typeof row === 'object') {
      productIdToUse = row.id || row._id;
    } else {
      productIdToUse = selectedProductId;
    }

    console.log('📍 productIdToUse:', productIdToUse);
    console.log('📍 token:', !!token);
    console.log('📍 requestId:', requestId);

    if (!productIdToUse || !token || !requestId) {
      console.log('❌ Missing required data, returning early');
      return;
    }

    const selectedProduct = products.find((p: any) => (p.id || p._id) === productIdToUse);
    console.log('📦 Found selected product:', selectedProduct);

    if (!selectedProduct) {
      console.log('❌ Product not found, returning');
      return;
    }

    const productName = selectedProduct.product_name || selectedProduct.name;
    console.log('🏷️ Product name:', productName);
    setSelectedProductId(productIdToUse);
    setSelectedProductName(productName);
    setSelectedProductDetails(selectedProduct);

    // Load brave_search_state if exists
    if (selectedProduct.brave_search_state) {
      const braveState = selectedProduct.brave_search_state;
      console.log('📊 Loading saved Brave Search state:', braveState);
      setBraveSearchOffset(braveState.offset || 0);
      if (braveState.search_fields_used) {
        const fields = braveState.search_fields_used;
        setBraveSearchFields({
          name: fields.includes('name'),
          brand: fields.includes('brand'),
          category: fields.includes('category'),
          description: fields.includes('description')
        });
      }
      if (braveState.additional_text) {
        setAdditionalSearchText(braveState.additional_text);
      }
    }

    // Mark as auto-loaded BEFORE navigating to prevent useEffect from re-loading
    setHasAutoLoaded(true);
    console.log('✅ Set hasAutoLoaded=true before navigation');

    // Navigate to URL with product ID
    onNavigate(`/dashboard/rfqs/open/${requestId}/suppliers/${productIdToUse}`);

    // Load suppliers
    await loadSuppliersForProduct(productIdToUse, productName, selectedProduct);
  };

  const handleBackToList = () => {
    console.log('🔙 Navigating back to product list');

    // Clean up states first
    setSearchMode('list');
    setSupplierProducts([]);
    setSelectedProductId(null);
    setSelectedProductName(null);
    setSelectedProductDetails(null);
    setSelectedSupplierIds([]);
    setBraveSearchOffset(0);
    setAdditionalSearchText('');
    // hasAutoLoaded will be reset by useEffect when productId changes

    // Then navigate
    onNavigate(`${baseRoute}/${requestId}/suppliers`);
  };

  // Handle approve AI supplier
  const handleApproveSupplier = async (row: any) => {
    console.log('✅ Approve supplier clicked:', row);

    if (!token || !row.supplier_id) {
      console.error('❌ Missing token or supplier_id');
      return;
    }

    try {
      // Fetch full supplier details
      const response = await fetch(API_ENDPOINTS.SUPPLIER_DETAILS(row.supplier_id), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();

      if (result.success && result.data) {
        console.log('✅ Loaded supplier details for approval:', result.data);
        setSupplierToApprove(result.data);
        setShowApproveModal(true);
      } else {
        alert('Failed to load supplier details');
      }
    } catch (error: any) {
      console.error('❌ Error loading supplier:', error);
      alert(`Error: ${error.message}`);
    }
  };

  // Handle approve supplier save
  const handleSaveApprovedSupplier = async () => {
    if (!supplierToApprove || !token) return;

    try {
      console.log('💾 Saving approved supplier:', supplierToApprove);

      const response = await fetch(API_ENDPOINTS.SUPPLIER_DETAILS(supplierToApprove._id), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: supplierToApprove.name,
          commercial_name: supplierToApprove.commercial_name,
          email: supplierToApprove.email,
          emails: Array.isArray(supplierToApprove.emails) ? supplierToApprove.emails : [supplierToApprove.email].filter(Boolean),
          phone: supplierToApprove.phone,
          website: supplierToApprove.website,
          has_website: !!supplierToApprove.website,
          address: supplierToApprove.address,
          contact_person: supplierToApprove.contact_person,
          region: supplierToApprove.region,
          categories: supplierToApprove.categories,
          category: supplierToApprove.category,
          tags: supplierToApprove.tags,
          online_sales: supplierToApprove.online_sales,
          mebsis_registration: supplierToApprove.mebsis_registration,
          notes: supplierToApprove.notes,
          source: 'approved',  // Change from ai_brave to approved
          approval_status: 'approved',
          is_approved: true,
          approved_by: 'current_user',  // Backend will use current_user from token
          approved_at: new Date().toISOString()
        })
      });

      const result = await response.json();

      if (result.success) {
        console.log('✅ Supplier approved successfully');
        alert('✅ Supplier approved successfully!');
        setShowApproveModal(false);
        setSupplierToApprove(null);
        setShowCategoryDropdown(false);

        // Reload supplier list
        if (selectedProductId && selectedProductName) {
          await loadSuppliersForProduct(selectedProductId, selectedProductName);
        }

        // Reload global supplier details for footer
        if (rfqSelectedSupplierIds.length > 0) {
          const detailsPromises = rfqSelectedSupplierIds.map(async (sid: string) => {
            try {
              const res = await fetch(API_ENDPOINTS.SUPPLIER_DETAILS(sid), {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              const r = await res.json();
              return r.success ? r.data : null;
            } catch (e) {
              return null;
            }
          });
          const details = (await Promise.all(detailsPromises)).filter(Boolean);
          setRfqSelectedSuppliersDetails(details);
        }
      } else {
        alert(`Failed: ${result.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('❌ Error approving supplier:', error);
      alert(`Error: ${error.message}`);
    }
  };

  // Handle Brave Search - All Pages Batch Mode
  const handleBraveSearchStream = async () => {
    if (!selectedProductName || !requestId || !token) {
      console.error('❌ Missing required data for Brave Search');
      return;
    }

    console.log('\n' + '='.repeat(80));
    console.log('🌐 BRAVE SEARCH BATCH - START');
    console.log('='.repeat(80));

    setIsBraveSearching(true);
    setStreamingProgress([]);
    setStreamingStatus('Initializing batch search...');
    setCurrentPage(0);
    setFoundCount(0);
    setDuplicateCount(0);

    // Declare polling variables outside try block for cleanup
    let pollInterval: ReturnType<typeof setInterval> | null = null;
    let pollCount = 0;
    const maxPollCount = 300; // 5 minutes max (300 * 0.5s = 2.5min)
    let searchCompleted = false;

    const stopPolling = () => {
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
    };

    try {
      const selectedFields = Object.entries(braveSearchFields)
        .filter(([_, enabled]) => enabled)
        .map(([field]) => field);

      if (selectedFields.length === 0) {
        alert('Please select at least one search field');
        setIsBraveSearching(false);
        return;
      }

      console.log('📡 Sending batch search request...');

      // Start search (async - don't wait for completion)
      const searchPromise = fetch(API_ENDPOINTS.BRAVE_SUPPLIER_SEARCH(requestId), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          request_product_id: selectedProductId,
          product_name: selectedProductName,
          search_fields: selectedFields,
          offset: 0,  // Always start from 0 in batch mode
          limit: 20,  // Max results per page (Brave API limit)
          additional_text: additionalSearchText.trim(),
          search_all_pages: true  // BATCH MODE FLAG
        })
      });

      const pollProgress = async () => {
        if (searchCompleted) return; // Don't poll if already completed

        try {
          const progressResponse = await fetch(
            API_ENDPOINTS.BRAVE_SUPPLIER_SEARCH_PROGRESS(requestId, selectedProductId!),
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );

          const progressResult = await progressResponse.json();

          if (progressResult.success && progressResult.data) {
            const progress = progressResult.data;

            // Update UI with latest progress
            setCurrentPage(progress.current_page || 0);
            setTotalPages(progress.total_pages || 10);
            setFoundCount(progress.found_count || 0);
            setDuplicateCount(progress.duplicate_count || 0);
            setStreamingStatus(progress.status || 'running');

            // Update progress log
            if (progress.progress_log && Array.isArray(progress.progress_log)) {
              setStreamingProgress(progress.progress_log);
            }

            // Check if complete
            if (progress.status === 'complete') {
              stopPolling();
              searchCompleted = true;

              // Wait for search promise to complete
              try {
                await searchPromise;
              } catch (e) {
                // Search may have already completed
              }

              // Reload data
              await loadProducts();
              if (selectedProductId) {
                await loadSuppliersForProduct(selectedProductId, selectedProductName);
              }

              setShowBraveModal(false);

              const message = progress.progress_log?.[progress.progress_log.length - 1]?.message || 'Search complete!';
              alert(`✅ ${message}`);

              return;
            }

            // Check for errors
            if (progress.error) {
              stopPolling();
              searchCompleted = true;
              setStreamingStatus('Error occurred');
              alert(`Failed: ${progress.error}`);
              return;
            }
          }

          pollCount++;
          if (pollCount >= maxPollCount) {
            stopPolling();
            searchCompleted = true;
            setStreamingStatus('Timeout - search may still be running');
            alert('Search is taking longer than expected. Please check back later.');
          }

        } catch (error: any) {
          console.error('❌ Error polling progress:', error);
          // Continue polling despite errors
        }
      };

      // Start polling immediately, then every 500ms
      pollProgress();
      pollInterval = setInterval(pollProgress, 500);

      // Wait for search to complete (in case it finishes before polling catches up)
      try {
        const searchResponse = await searchPromise;
        const searchResult = await searchResponse.json();
        console.log('📥 Search completed:', searchResult);

        // Give polling a moment to catch up
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Final poll to get latest progress
        if (!searchCompleted) {
          stopPolling();
          await pollProgress();
        }
      } catch (error: any) {
        console.error('❌ Error waiting for search:', error);
      }

    } catch (error: any) {
      console.error('❌ Error during batch search:', error);
      setStreamingStatus('Connection error');
      alert(`Error: ${error.message || 'Failed to search suppliers'}`);
    } finally {
      // Clean up polling interval
      stopPolling();
      setIsBraveSearching(false);
      console.log('='.repeat(80));
      console.log('🌐 BRAVE SEARCH BATCH - END');
      console.log('='.repeat(80) + '\n');
    }
  };

  // Handle Brave Search (Legacy - keep for fallback)
  const handleBraveSearch = async () => {
    if (!selectedProductName || !requestId || !token) {
      console.error('❌ Missing required data for Brave Search');
      return;
    }

    console.log('\n' + '='.repeat(80));
    console.log('🌐 BRAVE SEARCH - START');
    console.log('='.repeat(80));
    console.log('📦 Product:', selectedProductName);
    console.log('🔎 Search Fields:', Object.entries(braveSearchFields).filter(([_, v]) => v).map(([k]) => k));
    console.log('📄 Offset:', braveSearchOffset);
    console.log('📄 Limit:', braveSearchLimit);

    setIsBraveSearching(true);

    try {
      const selectedFields = Object.entries(braveSearchFields)
        .filter(([_, enabled]) => enabled)
        .map(([field]) => field);

      if (selectedFields.length === 0) {
        alert('Please select at least one search field');
        setIsBraveSearching(false);
        return;
      }

      console.log('📡 Sending request to backend...');
      console.log('📡 Brave Search API Call:');
      console.log('   - request_id:', requestId);
      console.log('   - request_product_id:', selectedProductId);
      console.log('   - product_name:', selectedProductName);

      const response = await fetch(API_ENDPOINTS.BRAVE_SUPPLIER_SEARCH(requestId), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          request_product_id: selectedProductId,  // IMPORTANT: Use request_product _id
          product_name: selectedProductName,
          search_fields: selectedFields,
          offset: braveSearchOffset,
          limit: braveSearchLimit,
          additional_text: additionalSearchText.trim()
        })
      });

      const result = await response.json();
      console.log('📥 Backend response:', result);

      if (result.success) {
        console.log('✅ Brave Search successful!');
        console.log('   - New suppliers:', result.data?.new_suppliers?.length || 0);
        console.log('   - New supplier_products:', result.data?.new_supplier_products?.length || 0);
        console.log('   - Total supplier_products:', result.data?.total_supplier_products);
        console.log('   - Scraped count:', result.data?.scraped_count);
        console.log('   - Formatted count:', result.data?.formatted_count);
        console.log('   - Blacklisted domains:', result.data?.blacklisted_domains);
        console.log('   - Next offset:', result.data?.next_offset);
        console.log('   - Search count:', result.data?.brave_search_state?.search_count);

        // Update offset for next search (from backend's saved state)
        const nextOffset = result.data?.brave_search_state?.offset || result.data?.next_offset || braveSearchOffset + braveSearchLimit;
        setBraveSearchOffset(nextOffset);
        console.log('📊 Updated offset to:', nextOffset);

        // Reload products list to get updated supplier_count
        await loadProducts();

        // Reload suppliers to show new results
        await loadSuppliersForProduct(selectedProductId!, selectedProductName);

        setShowBraveModal(false);

        const totalSuppliers = result.data?.total_supplier_products || 0;
        const newCount = result.data?.new_supplier_products?.length || 0;
        alert(`✅ Found ${newCount} new supplier products!\nTotal suppliers for this product: ${totalSuppliers}`);
      } else {
        console.error('❌ Brave Search failed:', result.error);
        alert(`Failed: ${result.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('❌ Error during Brave Search:', error);
      alert(`Error: ${error.message || 'Failed to search suppliers'}`);
    } finally {
      setIsBraveSearching(false);
      console.log('='.repeat(80));
      console.log('🌐 BRAVE SEARCH - END');
      console.log('='.repeat(80) + '\n');
    }
  };

  console.log('🎬 RENDER - Search Mode:', searchMode);
  console.log('🎬 RENDER - Supplier Products:', supplierProducts);

  // Determine if this is basic plan user route
  const isBasicPlanRoute = location.pathname.startsWith('/dashboard/user/rfq/');
  const Layout = isBasicPlanRoute ? UserLayout : AdminLayout;

  // Header actions for AdminLayout
  const selectedItemsCount = viewMode === 'suppliers' ? rfqSelectedSupplierIds.length : (viewMode === 'products_list' ? 0 : selectedSupplierIds.length);
  const headerActions = (
    <HeaderActions
      {...createStandardHeaderActions({
        isEditing: false,
        onEditToggle: () => { },
        onSaveChanges: () => { },
        onExport: () => setShowExportModal(true),
        onDelete: () => { },
        selectedItemsCount,
        itemsPerPage,
        onItemsPerPageChange: setItemsPerPage,
        onFiltersToggle: () => setShowFiltersModal(!showFiltersModal),
        useIndividualComponents: true,
        showEditButton: false,
        showDeleteButton: false,
        showExportButton: true,
        showAddButton: false,
        customButtons: viewMode === 'suppliers' ? [
          {
            label: '✨ Find New Suppliers (AI)',
            onClick: () => {
              // Switch to Product List Mode
              setViewMode('products_list');
              loadProducts();
            },
            type: 'primary',
          }
        ] : []
      })}
    />
  );

  return (
    <Layout
      user={user}
      currentRoute={currentRoute}
      onNavigate={onNavigate}
      onLogout={onLogout}
      pageTitle="Supplier Search"
      getDynamicSubItems={getDynamicSubItems}
      navigationActions={navigationActions}
      {...(!isBasicPlanRoute ? { headerActions } : {})}
    >
      {viewMode === 'suppliers' ? (
        // VIEW 1: SUPPLIER LIST (DEFAULT)
        <>
          <DataDashboard
            title="Suppliers"
            collection="suppliers"
            data={groupedSuppliers}
            columns={supplierColumns}
            user={user}
            currentRoute={currentRoute}
            onNavigate={onNavigate}
            onLogout={onLogout}
            onDataChange={() => { }}
            onDataFetch={handleDataFetch}
            onExport={handleExport}
            onSelectionChange={handleSelectionChange} // Reuse existing handleSelection logic if compatible, or create new one
            paginationInfo={{ ...paginationInfo, total: groupedSuppliers.length }}
            showAddButton={false}
            showSearch={true}
            showSelection={true}
            showEditButton={false}
            showDeleteButton={false}
            noLayout={true}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={setItemsPerPage}
            externalFiltersToggleState={showFiltersModal}
            onFiltersToggleStateChange={setShowFiltersModal}
            selectedItems={rfqSelectedSupplierIds}
          />

          {/* Global Selected Suppliers Footer - Show on supplier list page too */}
          {rfqSelectedSupplierIds.length > 0 && (
            <div style={{
              position: 'fixed',
              bottom: 0,
              left: '220px',
              right: 0,
              backgroundColor: '#f5f5f5',
              borderTop: '1px solid #e5e7eb',
              padding: '12px 24px',
              zIndex: 1000
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: '20px'
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#111827', marginBottom: '4px' }}>
                    Selected Suppliers (GLOBAL - All Products): {rfqSelectedSupplierIds.length} <span style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 400 }}>(Auto-saved)</span>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#4b5563', lineHeight: '1.4' }}>
                    {rfqSelectedSuppliersDetails.map((s, i) => (
                      <span key={s._id}>
                        {i + 1}. {s.supplier_readable_id} - {s.name || s.supplier_name}
                        {i < rfqSelectedSuppliersDetails.length - 1 ? '   ' : ''}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

        </>
      ) : viewMode === 'products_list' ? (
        // VIEW 2: PRODUCT LIST (FOR SEARCH)
        <>
          <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center' }}>
            <button
              onClick={() => setViewMode('suppliers')}
              style={{
                padding: '6px 12px',
                backgroundColor: '#fff',
                border: '1px solid #ccc',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: '#666',
                fontSize: '14px',
                fontWeight: 500
              }}
            >
              ← Back to Supplier List
            </button>
          </div>
          <DataDashboard
            title="Select Product to Search"
            collection="request_products"
            data={products}
            columns={productColumns}
            user={user}
            currentRoute={currentRoute}
            onNavigate={onNavigate}
            onLogout={onLogout}
            onDataChange={setProducts}
            onDataFetch={handleDataFetch}
            onExport={handleExport}
            onSelectionChange={handleSelectionChange}
            onOpen={handleOpen}
            paginationInfo={paginationInfo}
            showAddButton={false}
            showSearch={false}
            showSelection={true}
            showEditButton={false}
            showDeleteButton={false}
            noLayout={true}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={setItemsPerPage}
            externalFiltersToggleState={showFiltersModal}
            onFiltersToggleStateChange={setShowFiltersModal}
            selectedItems={selectedProductIds}
          />
        </>
      ) : (
        // VIEW 3: BRAVE SEARCH RESULTS
        <>
          <DataDashboard
            title={`Suppliers for: ${selectedProductName}`}
            collection="supplier_products"
            data={supplierProducts}
            columns={supplierProductColumns}
            user={user}
            currentRoute={currentRoute}
            onNavigate={onNavigate}
            onLogout={onLogout}
            onDataChange={setSupplierProducts}
            onDataFetch={handleDataFetch}
            onExport={handleExport}
            onSelectionChange={handleSelectionChange}
            paginationInfo={{ ...paginationInfo, total: supplierProducts.length }}
            showAddButton={false}
            showSearch={true}
            showSelection={true}
            showEditButton={true}
            showDeleteButton={false}
            noLayout={true}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={setItemsPerPage}
            externalFiltersToggleState={showFiltersModal}
            onFiltersToggleStateChange={setShowFiltersModal}
            selectedItems={supplierProducts.filter(sp => selectedSupplierIds.includes(String(sp.supplier_id))).map(sp => sp.supplier_product_id || sp._id || sp.id)}
            headerContent={
              <div style={{ display: 'flex', gap: '10px', marginLeft: 'auto' }}>
                <button
                  onClick={() => setShowBraveModal(true)}
                  disabled={isBraveSearching}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#f5f5f5',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    cursor: isBraveSearching ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isBraveSearching ? 'Searching...' : `Search with Brave (Page ${Math.floor(braveSearchOffset / braveSearchLimit) + 1})`}
                </button>
                <button
                  onClick={() => {
                    setViewMode('products_list');
                    setSelectedProductId(null);
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#f5f5f5',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  ← Back to Products
                </button>
              </div>
            }
          />

          {/* Reuse Footer Logic for Search View */}
          {rfqSelectedSupplierIds.length > 0 && supplierProducts.length > 0 && (() => {
            const selectedSuppliersInThisProduct = supplierProducts.filter((sp: any) =>
              rfqSelectedSupplierIds.includes(String(sp.supplier_id))
            );
            const uniqueSuppliers = selectedSuppliersInThisProduct.reduce((acc: any[], sp: any) => {
              if (!acc.find(s => s.supplier_id === sp.supplier_id)) {
                acc.push(sp);
              }
              return acc;
            }, []);

            return (
              <div style={{
                position: 'fixed',
                bottom: 0,
                left: '220px',
                right: 0,
                backgroundColor: '#f5f5f5',
                borderTop: '2px solid #ccc',
                padding: '15px 20px',
                zIndex: 1000
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: '20px'
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <strong>Selected Suppliers (GLOBAL): {rfqSelectedSupplierIds.length}</strong>
                    {uniqueSuppliers.length > 0 && (
                      <div style={{
                        fontSize: '12px',
                        color: '#666',
                        marginTop: '8px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '8px'
                      }}>
                        {uniqueSuppliers.map((sp: any, index: number) => (
                          <span key={sp.supplier_id} style={{ backgroundColor: '#e3f2fd', padding: '4px 8px', borderRadius: '4px' }}>
                            {sp.supplier_name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}
        </>
      )
      }

      {/* Approve AI Supplier Modal */}
      {
        showApproveModal && supplierToApprove && (
          <div
            onClick={() => setShowCategoryDropdown(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: 'white',
                padding: '24px',
                borderRadius: '8px',
                maxWidth: '600px',
                width: '90%',
                maxHeight: '90vh',
                overflow: 'auto',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
              }}
            >
              <h2 style={{ marginTop: 0, marginBottom: '20px' }}>
                Approve AI Supplier
              </h2>

              <div style={{ marginBottom: '20px' }}>
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '16px' }}>
                  Review and edit the supplier information below, then click "Approve Supplier" to confirm.
                </p>

                <div style={{ display: 'grid', gap: '12px', gridTemplateColumns: '1fr 1fr' }}>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 'bold' }}>
                      Company Name *
                    </label>
                    <input
                      type="text"
                      value={supplierToApprove.name || ''}
                      onChange={(e) => setSupplierToApprove({ ...supplierToApprove, name: e.target.value })}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 'bold' }}>
                      Commercial Name
                    </label>
                    <input
                      type="text"
                      value={supplierToApprove.commercial_name || ''}
                      onChange={(e) => setSupplierToApprove({ ...supplierToApprove, commercial_name: e.target.value })}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 'bold' }}>
                      Website
                    </label>
                    <input
                      type="text"
                      value={supplierToApprove.website || ''}
                      onChange={(e) => setSupplierToApprove({ ...supplierToApprove, website: e.target.value })}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 'bold' }}>
                      Email
                    </label>
                    <input
                      type="email"
                      value={supplierToApprove.email || ''}
                      onChange={(e) => setSupplierToApprove({ ...supplierToApprove, email: e.target.value })}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 'bold' }}>
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={supplierToApprove.phone || ''}
                      onChange={(e) => setSupplierToApprove({ ...supplierToApprove, phone: e.target.value })}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                  </div>

                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 'bold' }}>
                      Address
                    </label>
                    <input
                      type="text"
                      value={supplierToApprove.address || ''}
                      onChange={(e) => setSupplierToApprove({ ...supplierToApprove, address: e.target.value })}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 'bold' }}>
                      Contact Person
                    </label>
                    <input
                      type="text"
                      value={supplierToApprove.contact_person || ''}
                      onChange={(e) => setSupplierToApprove({ ...supplierToApprove, contact_person: e.target.value })}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 'bold' }}>
                      Region
                    </label>
                    <input
                      type="text"
                      value={supplierToApprove.region || ''}
                      onChange={(e) => setSupplierToApprove({ ...supplierToApprove, region: e.target.value })}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 'bold' }}>
                      MEBSIS Registration
                    </label>
                    <select
                      value={supplierToApprove.mebsis_registration || 'Pending'}
                      onChange={(e) => setSupplierToApprove({ ...supplierToApprove, mebsis_registration: e.target.value })}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                    >
                      <option value="Active">Active</option>
                      <option value="Pending">Pending</option>
                      <option value="Not Required">Not Required</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 'bold' }}>
                      Online Sales
                    </label>
                    <input
                      type="checkbox"
                      checked={supplierToApprove.online_sales || false}
                      onChange={(e) => setSupplierToApprove({ ...supplierToApprove, online_sales: e.target.checked })}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                  </div>

                  <div style={{ gridColumn: '1 / -1', position: 'relative' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 'bold' }}>
                      Categories (UNSPSC Codes)
                    </label>

                    {/* Selected categories as tags */}
                    <div
                      onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                      style={{
                        width: '100%',
                        minHeight: '40px',
                        padding: '6px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '6px',
                        alignItems: 'center',
                        backgroundColor: '#fff'
                      }}
                    >
                      {(() => {
                        // Filter out invalid categories (non-UNSPSC codes)
                        const rawCategories = Array.isArray(supplierToApprove.category) ? supplierToApprove.category : (supplierToApprove.category ? [supplierToApprove.category] : []);
                        const validCategories = rawCategories.filter((cat: string) => cat.match(/^\d{8}$/));

                        if (validCategories.length === 0) {
                          return <span style={{ color: '#999', fontSize: '13px' }}>Click to select categories...</span>;
                        }

                        return validCategories.map((catCode: string) => {
                          const catName = getCategoryName(catCode);
                          return (
                            <span key={catCode} style={{
                              backgroundColor: '#e3f2fd',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}>
                              {catName}
                              <span
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const newCategories = validCategories.filter((c: string) => c !== catCode);
                                  setSupplierToApprove({ ...supplierToApprove, category: newCategories });
                                }}
                                style={{
                                  cursor: 'pointer',
                                  fontWeight: 'bold',
                                  color: '#666',
                                  fontSize: '14px'
                                }}
                              >
                                ×
                              </span>
                            </span>
                          );
                        });
                      })()}
                    </div>

                    {/* Dropdown - UNSPSC Categories */}
                    {showCategoryDropdown && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        marginTop: '4px',
                        maxHeight: '200px',
                        overflow: 'auto',
                        zIndex: 1000,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }}>
                        {[
                          { code: '12000000', name: 'Kimyasal Maddeler ve Gaz Malzemeleri' },
                          { code: '27000000', name: 'Araçlar ve Genel Makine Ekipmanları' },
                          { code: '39000000', name: 'Elektrik Sistemleri ve Aydınlatma' },
                          { code: '41000000', name: 'Laboratuvar ve Ölçüm Ekipmanları' },
                          { code: '42000000', name: 'Tıbbi Ekipmanlar ve Aksesuarlar' },
                          { code: '44000000', name: 'Ofis Ekipmanları ve Aksesuarlar' },
                          { code: '50000000', name: 'Gıda ve İçecek Ürünleri' },
                          { code: '51000000', name: 'İlaçlar ve Eczacılık Ürünleri' },
                          { code: '53000000', name: 'Giyim ve Kişisel Bakım Ürünleri' },
                          { code: '56000000', name: 'Mobilya ve Ev Eşyaları' },
                          { code: '72000000', name: 'Bina ve Tesis İnşaat Hizmetleri' },
                          { code: '81000000', name: 'Mühendislik ve Teknoloji Hizmetleri' },
                          { code: '99999999', name: 'Diğer' }
                        ].map(cat => {
                          const categories = Array.isArray(supplierToApprove.category) ? supplierToApprove.category : (supplierToApprove.category ? [supplierToApprove.category] : []);
                          const isSelected = categories.includes(cat.code);

                          return (
                            <div
                              key={cat.code}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isSelected) {
                                  const newCategories = categories.filter((c: string) => c !== cat.code);
                                  setSupplierToApprove({ ...supplierToApprove, category: newCategories });
                                } else {
                                  setSupplierToApprove({ ...supplierToApprove, category: [...categories, cat.code] });
                                }
                              }}
                              style={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                backgroundColor: isSelected ? '#e3f2fd' : 'white',
                                borderBottom: '1px solid #f0f0f0',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                readOnly
                                style={{ cursor: 'pointer' }}
                              />
                              <span>{cat.name}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 'bold' }}>
                      Tags (comma-separated)
                    </label>
                    <input
                      type="text"
                      value={Array.isArray(supplierToApprove.tags) ? supplierToApprove.tags.join(', ') : (supplierToApprove.tags || '')}
                      onChange={(e) => setSupplierToApprove({ ...supplierToApprove, tags: e.target.value.split(',').map((t: string) => t.trim()) })}
                      placeholder="e.g., medical, wholesale, certified"
                      style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                  </div>

                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 'bold' }}>
                      Notes
                    </label>
                    <textarea
                      value={supplierToApprove.notes || ''}
                      onChange={(e) => setSupplierToApprove({ ...supplierToApprove, notes: e.target.value })}
                      rows={3}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowApproveModal(false);
                    setSupplierToApprove(null);
                    setShowCategoryDropdown(false);
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#f5f5f5',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveApprovedSupplier}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  Approve Supplier
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Brave Search Modal */}
      {
        showBraveModal && (() => {
          console.log('🎨 MODAL DEBUG - selectedProductDetails:', selectedProductDetails);
          console.log('🎨 MODAL DEBUG - Fields:', {
            name: selectedProductDetails?.product_name || selectedProductDetails?.name,
            brand: selectedProductDetails?.brand || selectedProductDetails?.product_brand,
            category: selectedProductDetails?.category,
            categoryMapped: selectedProductDetails?.category ? (
              Array.isArray(selectedProductDetails.category)
                ? selectedProductDetails.category.map((c: string) => getCategoryName(c))
                : getCategoryName(selectedProductDetails.category)
            ) : null,
            description: selectedProductDetails?.description
          });
          return null;
        })()
      }
      {
        showBraveModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '8px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}>
              <h2 style={{ marginTop: 0, marginBottom: '20px' }}>
                Brave Search Settings
              </h2>

              <div style={{ marginBottom: '20px' }}>
                <p style={{ marginBottom: '10px', fontWeight: 'bold' }}>
                  Product: {selectedProductName}
                </p>
                <div style={{ fontSize: '14px', color: '#666' }}>
                  {selectedProductDetails?.supplier_count !== undefined && (
                    <p style={{ marginBottom: '5px' }}>
                      Current Suppliers: <strong>{selectedProductDetails.supplier_count}</strong>
                    </p>
                  )}
                  {selectedProductDetails?.brave_search_state?.search_count && (
                    <p style={{ marginBottom: '5px' }}>
                      Previous Searches: <strong>{selectedProductDetails.brave_search_state.search_count}</strong> times
                    </p>
                  )}
                  <p style={{ marginBottom: '5px', fontSize: '13px', color: '#666', fontStyle: 'italic' }}>
                    This search will scan all available pages (0-9) at once
                  </p>
                </div>
              </div>

              {/* Search Query Preview */}
              <div style={{
                marginBottom: '20px',
                padding: '12px',
                backgroundColor: '#f0f7ff',
                borderLeft: '4px solid #2196F3',
                borderRadius: '4px'
              }}>
                <p style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '13px', color: '#333' }}>
                  Search Query Preview:
                </p>
                <p style={{ fontSize: '14px', color: '#333', fontFamily: 'monospace', wordBreak: 'break-word' }}>
                  {(() => {
                    const queryParts = [];

                    // Name
                    const name = selectedProductDetails?.product_name || selectedProductDetails?.name;
                    if (braveSearchFields.name && name) {
                      queryParts.push(name);
                    }

                    // Brand
                    const brand = selectedProductDetails?.brand || selectedProductDetails?.product_brand;
                    if (braveSearchFields.brand && brand) {
                      queryParts.push(brand);
                    }

                    // Category (use local mapping)
                    if (braveSearchFields.category && selectedProductDetails?.category) {
                      const cats = Array.isArray(selectedProductDetails.category)
                        ? selectedProductDetails.category
                        : [selectedProductDetails.category];
                      const categoryTexts = cats
                        .map((c: string) => getCategoryName(c))
                        .filter((c: string) => !c.match(/^\d+$/));  // Skip if still numbers
                      if (categoryTexts.length > 0) {
                        queryParts.push(categoryTexts.join(' '));
                      }
                    }

                    // Description
                    if (braveSearchFields.description && selectedProductDetails?.description) {
                      const desc = selectedProductDetails.description.substring(0, 50);
                      queryParts.push(desc + (selectedProductDetails.description.length > 50 ? '...' : ''));
                    }

                    // Additional text
                    if (additionalSearchText.trim()) {
                      queryParts.push(additionalSearchText.trim());
                    }

                    return queryParts.length > 0 ? `"${queryParts.join(' ')} tedarikçi"` : '(Select at least one field)';
                  })()}
                </p>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <p style={{ fontWeight: 'bold', marginBottom: '10px' }}>
                  Select fields to include in search:
                </p>

                <label style={{ display: 'block', marginBottom: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={braveSearchFields.name}
                    onChange={(e) => setBraveSearchFields(prev => ({ ...prev, name: e.target.checked }))}
                    style={{ marginRight: '8px' }}
                  />
                  <span style={{ fontWeight: 500 }}>Product Name</span>
                  {(() => {
                    const name = selectedProductDetails?.product_name || selectedProductDetails?.name;
                    return name ? (
                      <span style={{ marginLeft: '8px', color: '#4CAF50', fontSize: '13px' }}>
                        ({name})
                      </span>
                    ) : (
                      <span style={{ marginLeft: '8px', color: '#999', fontSize: '13px', fontStyle: 'italic' }}>
                        (Not available)
                      </span>
                    );
                  })()}
                </label>

                <label style={{ display: 'block', marginBottom: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={braveSearchFields.brand}
                    onChange={(e) => setBraveSearchFields(prev => ({ ...prev, brand: e.target.checked }))}
                    style={{ marginRight: '8px' }}
                  />
                  <span style={{ fontWeight: 500 }}>Brand</span>
                  {(() => {
                    const brand = selectedProductDetails?.brand || selectedProductDetails?.product_brand;
                    return brand ? (
                      <span style={{ marginLeft: '8px', color: '#4CAF50', fontSize: '13px' }}>
                        ({brand})
                      </span>
                    ) : (
                      <span style={{ marginLeft: '8px', color: '#999', fontSize: '13px', fontStyle: 'italic' }}>
                        (Not available)
                      </span>
                    );
                  })()}
                </label>

                <label style={{ display: 'block', marginBottom: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={braveSearchFields.category}
                    onChange={(e) => setBraveSearchFields(prev => ({ ...prev, category: e.target.checked }))}
                    style={{ marginRight: '8px' }}
                  />
                  <span style={{ fontWeight: 500 }}>Category</span>
                  {selectedProductDetails?.category ? (
                    <>
                      <span style={{ marginLeft: '8px', color: '#FF9800', fontSize: '13px' }}>
                        ({Array.isArray(selectedProductDetails.category)
                          ? selectedProductDetails.category.map((c: string) => getCategoryName(c)).join(', ')
                          : getCategoryName(selectedProductDetails.category)})
                      </span>
                      {Array.isArray(selectedProductDetails.category) && selectedProductDetails.category.every((c: string) => c.match(/^\d+$/)) && (
                        <span style={{ marginLeft: '8px', fontSize: '11px', color: '#666', display: 'block', marginTop: '4px' }}>
                          ℹ️ UNSPSC codes mapped to Turkish names
                        </span>
                      )}
                    </>
                  ) : (
                    <span style={{ marginLeft: '8px', color: '#999', fontSize: '13px', fontStyle: 'italic' }}>
                      (Not available)
                    </span>
                  )}
                </label>

                <label style={{ display: 'block', marginBottom: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={braveSearchFields.description}
                    onChange={(e) => setBraveSearchFields(prev => ({ ...prev, description: e.target.checked }))}
                    style={{ marginRight: '8px' }}
                  />
                  <span style={{ fontWeight: 500 }}>Description</span>
                  {selectedProductDetails?.description ? (
                    <span style={{ marginLeft: '8px', color: '#4CAF50', fontSize: '13px' }}>
                      ({selectedProductDetails.description.length > 50
                        ? selectedProductDetails.description.substring(0, 50) + '...'
                        : selectedProductDetails.description})
                    </span>
                  ) : (
                    <span style={{ marginLeft: '8px', color: '#999', fontSize: '13px', fontStyle: 'italic' }}>
                      (Not available)
                    </span>
                  )}
                </label>

                {/* Additional search text input */}
                <div style={{ marginTop: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, fontSize: '13px' }}>
                    Additional search terms (optional):
                  </label>
                  <input
                    type="text"
                    value={additionalSearchText}
                    onChange={(e) => setAdditionalSearchText(e.target.value)}
                    placeholder="e.g., wholesale, bulk, medical grade..."
                    disabled={isBraveSearching}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                  <p style={{ fontSize: '11px', color: '#666', marginTop: '4px', marginBottom: 0 }}>
                    This text will be added to the search query
                  </p>
                </div>
              </div>

              {/* Streaming Progress Display */}
              {isBraveSearching && (
                <div style={{
                  marginTop: '20px',
                  padding: '16px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #dee2e6'
                }}>
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '8px'
                    }}>
                      <span style={{ fontSize: '14px', fontWeight: 600 }}>
                        Progress: Page {currentPage}/{totalPages}
                      </span>
                      <span style={{ fontSize: '13px', color: '#666' }}>
                        Found: {foundCount} | Duplicates: {duplicateCount}
                      </span>
                    </div>

                    {/* Progress bar */}
                    <div style={{
                      width: '100%',
                      height: '8px',
                      backgroundColor: '#e9ecef',
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${(currentPage / totalPages) * 100}%`,
                        height: '100%',
                        backgroundColor: '#2196F3',
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                  </div>

                  {/* Current Status */}
                  <div style={{
                    fontSize: '13px',
                    color: '#495057',
                    marginBottom: '12px',
                    fontWeight: 500
                  }}>
                    {streamingStatus}
                  </div>

                  {/* Live Log */}
                  <div style={{
                    maxHeight: '200px',
                    overflowY: 'auto',
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    backgroundColor: '#fff',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #e9ecef'
                  }}>
                    {streamingProgress.slice(-15).map((item, idx) => (
                      <div key={idx} style={{
                        padding: '4px 0',
                        borderBottom: idx < streamingProgress.length - 1 ? '1px solid #f1f3f5' : 'none',
                        color: item.type === 'error' ? '#dc3545' :
                          item.type === 'complete' ? '#28a745' : '#495057'
                      }}>
                        {item.message}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowBraveModal(false)}
                  disabled={isBraveSearching}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#f5f5f5',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    cursor: isBraveSearching ? 'not-allowed' : 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleBraveSearchStream}
                  disabled={isBraveSearching}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#2196F3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isBraveSearching ? 'not-allowed' : 'pointer',
                    opacity: isBraveSearching ? 0.6 : 1
                  }}
                >
                  {isBraveSearching ? `Searching ${currentPage}/${totalPages}...` : 'Search All Pages (0-9)'}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        collection={searchMode === 'list' ? 'request_products' : 'supplier_products'}
        selectedItems={searchMode === 'list'
          ? products.filter(p => selectedProductIds.includes(p.id || p._id))
          : supplierProducts.filter(sp => selectedSupplierIds.includes(String(sp.supplier_id)))
        }
        customFields={searchMode === 'list'
          ? productColumns.map(col => ({
            name: col.key,
            type: col.type,
            description: col.title
          }))
          : supplierProductColumns.filter(col => col.key !== 'select').map(col => ({
            name: col.key,
            type: col.type,
            description: col.title
          }))
        }
        onExport={async (exportData: any) => {
          try {
            const response = await fetch(API_ENDPOINTS.EXPORT_DATA, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(exportData)
            });

            if (!response.ok) {
              throw new Error('Export failed');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${exportData.filename || 'export'}.${exportData.format === 'csv' ? 'csv' : 'xlsx'}`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            setShowExportModal(false);
          } catch (error: any) {
            console.error('Export error:', error);
            alert(`Export failed: ${error.message || 'Unknown error'}`);
          }
        }}
      />
    </Layout >
  );
};

export default RFQSupplierSearchDashboard;

